import React, { useCallback, useEffect, useRef, useState } from "react";
import InfiniteScrollComponent from "../../Components/InfiniteScrollComponent";
import {
  useLazyGetCampaignUsersQuery,
  useSendCampaignToUsersMutation,
} from "../../Data/services/engagementApi";
import { ErrorToast, SuccessToast } from "../../Hooks/useToast";
import UsersTableSkeleton from "../Dashboard/components/UsersTableSkeleton";
import { extractLinks, hasNextPage } from "../Dashboard/utils";
import AudienceTabs from "./components/AudienceTabs";
import CampaignUsersHeader from "./components/CampaignUsersHeader";
import CampaignUsersTable from "./components/CampaignUsersTable";
import CampaignUsersToolbar from "./components/CampaignUsersToolbar";
import { ROUTES } from "./Route";
import styles from "./styles/campaignNotifications.module.scss";

const PAGE_SIZE = 50;
const SEND_REFRESH_DELAY_MS = 2000;

export default function CampaignUsersScreen({
  campaign,
  initialAudience = "sent",
  setPage,
  setAudience: setParentAudience,
}) {
  const scrollRef = useRef(null);
  const pageRef = useRef(1);
  const loadedCountRef = useRef(0);
  const refreshTimerRef = useRef(null);

  const [audience, setAudience] = useState(initialAudience);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedSlugs, setSelectedSlugs] = useState(() => new Set());
  const [excludedIds, setExcludedIds] = useState(() => new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [lastPageSize, setLastPageSize] = useState(null);
  const [refreshingAfterSend, setRefreshingAfterSend] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [fetchCampaignUsers, { data, isLoading, isFetching, isError, isSuccess }] =
    useLazyGetCampaignUsersQuery();
  const [sendCampaignToUsers, { isLoading: isSending }] =
    useSendCampaignToUsersMutation();

  const loadPage = useCallback(
    async (page, limit = PAGE_SIZE) => {
      if (!campaign?.slug) return;

      try {
        const response = await fetchCampaignUsers({
          slug: campaign.slug,
          audience,
          page,
          limit,
          ...(search ? { search } : {}),
          ...(refreshKey ? { refreshKey } : {}),
        }).unwrap();
        const pageUsers = response?.data?.users ?? [];
        const mergedCount = Array.isArray(pageUsers) ? pageUsers.length : 0;
        setLastPageSize(
          page === 1
            ? mergedCount
            : Math.max(0, mergedCount - loadedCountRef.current)
        );
        loadedCountRef.current = mergedCount;
        pageRef.current = page;
      } catch (err) {
        ErrorToast(err?.data?.message || "Failed to load campaign users");
        throw err;
      }
    },
    [audience, campaign?.slug, fetchCampaignUsers, refreshKey, search]
  );

  useEffect(() => {
    if (!campaign?.slug) {
      setPage(ROUTES.LIST);
      return undefined;
    }

    pageRef.current = 1;
    loadedCountRef.current = 0;
    setLastPageSize(null);
    loadPage(1, PAGE_SIZE)
      .catch(() => {})
      .finally(() => {
        setRefreshingAfterSend(false);
      });
    // Omit loadPage from deps so its identity changes do not reset to page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.slug, audience, search, refreshKey, setPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim().length >= 3 ? searchInput.trim() : "";
      setSearch((prev) => (prev === next ? prev : next));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const pagination = extractLinks(data);
  const payload = data?.data ?? {};
  const users = Array.isArray(payload.users) ? payload.users : [];
  const paginationCurrent = pagination.current;
  const paginationNext = pagination.next;
  const perPage = pagination.per_page || PAGE_SIZE;
  const canLoadMore = hasNextPage(pagination, {
    loadedCount: users.length,
    lastPageSize,
    perPage,
  });
  const hasNext = !isError && users.length > 0 && canLoadMore;
  const showInitialSkeleton = (isLoading || isFetching) && users.length === 0;
  const showEmpty = isSuccess && !isFetching && users.length === 0;
  const isLoadingMore = isFetching && users.length > 0;
  const selectable = audience === "eligible" || audience === "all";
  const sendLocked = isSending || refreshingAfterSend;
  const canSend =
    selectable && (isSelectAll || selectedSlugs.size > 0) && !sendLocked;

  const handleNext = useCallback(async () => {
    if (isFetching || !canLoadMore) return;
    const nextPage =
      paginationNext > paginationCurrent
        ? paginationNext
        : pageRef.current + 1;
    await loadPage(nextPage, perPage);
  }, [
    canLoadMore,
    isFetching,
    loadPage,
    paginationCurrent,
    paginationNext,
    perPage,
  ]);

  const handleAudienceChange = (nextAudience) => {
    if (nextAudience === audience) return;
    setSelectedSlugs(new Set());
    setExcludedIds(new Set());
    setIsSelectAll(false);
    setAudience(nextAudience);
    setParentAudience?.(nextAudience);
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    setSelectedSlugs(new Set());
    setExcludedIds(new Set());
    setIsSelectAll(false);
  };

  const handleToggle = (slug) => {
    if (!slug) return;

    if (isSelectAll) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(slug)) next.delete(slug);
        else next.add(slug);
        return next;
      });
      return;
    }

    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleSelectAll = () => {
    setIsSelectAll((prev) => !prev);
    setSelectedSlugs(new Set());
    setExcludedIds(new Set());
  };

  const handleSend = async () => {
    if (!canSend || !campaign?.slug) return;

    const excluded = Array.from(excludedIds);
    const payload = isSelectAll
      ? {
          campaignSlug: campaign.slug,
          isSelectAll: true,
          ...(audience === "eligible" ? { eligible: true } : {}),
          ...(excluded.length > 0 ? { excludedIds: excluded } : {}),
        }
      : {
          campaignSlug: campaign.slug,
          user_slugs: Array.from(selectedSlugs),
        };

    try {
      const result = await sendCampaignToUsers(payload).unwrap();
      const queued = result?.data?.queued ?? result?.queued;
      const countLabel = isSelectAll
        ? queued != null
          ? queued
          : "all"
        : payload.user_slugs.length;
      SuccessToast(`Send queued for ${countLabel} users`);
      setSelectedSlugs(new Set());
      setExcludedIds(new Set());
      setIsSelectAll(false);
      setRefreshingAfterSend(true);

      refreshTimerRef.current = setTimeout(() => {
        setAudience("sent");
        setParentAudience?.("sent");
        setRefreshKey(Date.now());
      }, SEND_REFRESH_DELAY_MS);
    } catch (err) {
      const message = err?.data?.message;
      if (err?.status === 429) {
        ErrorToast(
          message || "A large send is already running. Try again later."
        );
        return;
      }
      ErrorToast(message || "Failed to queue campaign send");
    }
  };

  return (
    <div className={`${styles.MainContainer} ${styles.usersLayout}`}>
      <CampaignUsersHeader
        campaign={campaign}
        search={searchInput}
        onSearch={handleSearchChange}
        onBack={() => setPage(ROUTES.LIST)}
      />

      <div className={styles.audienceBar}>
        <AudienceTabs audience={audience} onChange={handleAudienceChange} />
        <CampaignUsersToolbar
          audience={audience}
          selectedCount={selectedSlugs.size}
          excludedCount={excludedIds.size}
          isSelectAll={isSelectAll}
          canSend={canSend}
          isSending={sendLocked}
          onSelectAll={handleSelectAll}
          onSend={handleSend}
        />
      </div>

      <div className={styles.usersScroll} ref={scrollRef}>
        {isError && users.length === 0 ? (
          <p className="text-danger m-3">Failed to load campaign users.</p>
        ) : showEmpty ? (
          <div className={styles.emptyState}>
            {search
              ? `No users match “${search}”.`
              : audience === "eligible"
                ? "No eligible users found for this campaign."
                : audience === "all"
                  ? "No users found."
                  : "No sent users found for this campaign."}
          </div>
        ) : showInitialSkeleton ? (
          <UsersTableSkeleton rows={8} columns={selectable ? 5 : 4} />
        ) : (
          <InfiniteScrollComponent
            next={handleNext}
            hasNext={hasNext}
            isLoading={isLoadingMore}
            rootRef={scrollRef}
            skeleton={<UsersTableSkeleton rows={3} columns={selectable ? 5 : 4} />}
          >
            <CampaignUsersTable
              users={users}
              audience={audience}
              selectedSlugs={selectedSlugs}
              excludedIds={excludedIds}
              isSelectAll={isSelectAll}
              onToggle={handleToggle}
            />
          </InfiniteScrollComponent>
        )}
      </div>
    </div>
  );
}
